import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Stack,
  Pagination,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import alertsApiService from '../../services/alerts/AlertsApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentFilter, setAgentFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 50,
    count: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    filterAlerts();
  }, [alerts, searchTerm, agentFilter, levelFilter]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [alertsData, agentsData] = await Promise.all([
        alertsApiService.getAllAlerts({ page: 1, size: 50 }),
        dashboardApiService.getAllAgents()
      ]);
      
      setAlerts(alertsData.alerts || []);
      setAgents(agentsData);
      setPagination({
        total: alertsData.total || 0,
        page: alertsData.page || 1,
        size: alertsData.size || 50,
        count: alertsData.count || 0
      });
    } catch (err) {
      setError('Failed to fetch alerts data');
      console.error('Alerts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      let alertsData;
      if (agentFilter === 'all') {
        alertsData = await alertsApiService.getAllAlerts({ page, size: 50 });
      } else {
        alertsData = await alertsApiService.getAlertsByAgent(agentFilter, { page, size: 50 });
      }
      
      // Handle the API response structure
      setAlerts(alertsData.alerts || []);
      setPagination({
        total: alertsData.total || 0,
        page: alertsData.page || page,
        size: alertsData.size || 50,
        count: alertsData.count || 0
      });
    } catch (err) {
      setError('Failed to fetch alerts');
      console.error('Alerts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAlerts = () => {
    let filtered = alerts;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(alert =>
        alert.rule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.agent.ip.includes(searchTerm) ||
        alert.rule.id.toString().includes(searchTerm) ||
        alert.full_log.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by alert level
    if (levelFilter !== 'all') {
      const level = parseInt(levelFilter);
      filtered = filtered.filter(alert => alert.rule.level === level);
    }

    setFilteredAlerts(filtered);
  };

  const handleAgentFilterChange = async (agentId) => {
    setAgentFilter(agentId);
    await fetchAlerts(1); // Reset to first page when changing filters
  };

  const handlePageChange = async (event, newPage) => {
    await fetchAlerts(newPage);
  };

  const getLevelColor = (level) => {
    if (level <= 3) return 'info';
    if (level <= 5) return 'warning';
    if (level <= 7) return 'error';
    return 'error';
  };

  const getLevelIcon = (level) => {
    if (level <= 3) return <InfoIcon className="level-icon-info" />;
    if (level <= 5) return <WarningIcon className="level-icon-warning" />;
    if (level <= 7) return <ErrorIcon className="level-icon-error" />;
    return <ErrorIcon className="level-icon-critical" />;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getUniqueAlertLevels = () => {
    const levels = [...new Set(alerts.map(alert => alert.rule.level))].sort((a, b) => a - b);
    return levels;
  };

  if (loading) {
    return (
      <Box className="alerts-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading alerts...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="alerts-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchInitialData}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="alerts">
      <Box className="alerts-header">
        <Typography variant="h4" className="alerts-title">
          Security Alerts
        </Typography>
        <Typography variant="body1" className="alerts-subtitle">
          Monitor and analyze security alerts from all agents
        </Typography>
      </Box>

      {/* Filters and Controls */}
      <Box className="alerts-controls">
        <Box className="alerts-filters">
          <FormControl className="filter-dropdown">
            <InputLabel>Agent Filter</InputLabel>
            <Select
              value={agentFilter}
              onChange={(e) => handleAgentFilterChange(e.target.value)}
              label="Agent Filter"
            >
              <MenuItem value="all">All Agents</MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name} (ID: {agent.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl className="filter-dropdown">
            <InputLabel>Alert Level</InputLabel>
            <Select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              label="Alert Level"
            >
              <MenuItem value="all">All Levels</MenuItem>
              {getUniqueAlertLevels().map((level) => (
                <MenuItem key={level} value={level.toString()}>
                  Level {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            placeholder="Search alerts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="alerts-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Box className="alerts-stats">
          <Chip 
            label={`Total: ${pagination.total}`} 
            className="stat-chip total"
          />
          <Chip 
            label={`Showing: ${pagination.count}`} 
            className="stat-chip showing"
          />
          <Chip 
            label={`Filtered: ${filteredAlerts.length}`} 
            className="stat-chip filtered"
          />
          <Chip 
            label={`High Level: ${alerts.filter(a => a.rule.level >= 7).length}`} 
            className="stat-chip high-level"
          />
        </Box>
      </Box>

      {/* Alerts Table View */}
      <TableContainer component={Paper} className="alerts-table-container">
        <Table className="alerts-table">
          <TableHead>
            <TableRow className="table-header">
              <TableCell className="table-header-cell">Alert</TableCell>
              <TableCell className="table-header-cell">Level</TableCell>
              <TableCell className="table-header-cell">Agent</TableCell>
              <TableCell className="table-header-cell">Rule</TableCell>
              <TableCell className="table-header-cell">Location</TableCell>
              <TableCell className="table-header-cell">Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAlerts.map((alert) => (
              <TableRow 
                key={alert.id}
                className="table-row"
              >
                <TableCell className="table-cell alert-description-cell">
                  <div className="alert-description-container">
                    <Typography className="alert-description">
                      {alert.rule?.description || 'No description available'}
                    </Typography>
                    {alert.full_log && (
                      <Typography className="alert-log">
                        {truncateText(alert.full_log, 80)}
                      </Typography>
                    )}
                    <div className="alert-groups">
                      {alert.rule?.groups && alert.rule.groups.slice(0, 3).map((group, idx) => (
                        <Chip key={idx} label={group} size="small" className="group-tag" />
                      ))}
                      {alert.rule?.groups && alert.rule.groups.length > 3 && (
                        <span className="more-groups">+{alert.rule.groups.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="level-container">
                    {getLevelIcon(alert.rule.level)}
                    <Chip 
                      label={`Level ${alert.rule.level}`}
                      color={getLevelColor(alert.rule.level)}
                      size="small"
                      className="level-chip"
                    />
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="agent-info-container">
                    <Box className="agent-name-ip">
                      <Typography variant="body2" className="agent-name">
                        {alert.agent.name}
                      </Typography>
                      <Typography variant="caption" className="agent-ip">
                        IP: {alert.agent.ip}
                      </Typography>
                    </Box>
                    <Typography variant="caption" className="agent-id">
                      ID: {alert.agent.id}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="rule-info-container">
                    <Typography variant="body2" className="rule-id">
                      Rule ID: {alert.rule.id}
                    </Typography>
                    <Typography variant="caption" className="rule-fired">
                      Fired: {alert.rule.firedtimes} times
                    </Typography>
                    {(alert.rule.pci_dss || alert.rule.nist_800_53 || alert.rule.gdpr) && (
                      <Typography variant="caption" className="mitre-info">
                        {alert.rule.pci_dss ? `PCI DSS: ${alert.rule.pci_dss[0]}` : 
                         alert.rule.nist_800_53 ? `NIST: ${alert.rule.nist_800_53[0]}` :
                         alert.rule.gdpr ? `GDPR: ${alert.rule.gdpr[0]}` : 'N/A'}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="location-info-container">
                    <Typography variant="body2" className="location-path">
                      {alert.location}
                    </Typography>
                    {alert.GeoLocation && (
                      <Box className="geo-location">
                        <LocationOnIcon className="location-icon" />
                        <Typography variant="caption" className="geo-info">
                          {alert.GeoLocation.city_name ? 
                            `${alert.GeoLocation.city_name}, ${alert.GeoLocation.country_name}` : 
                            alert.GeoLocation.country_name || 'Unknown'
                          }
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="timestamp-container">
                    <ScheduleIcon className="timestamp-icon" />
                    <Typography variant="body2" className="timestamp">
                      {formatTimestamp(alert.timestamp)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {pagination.total > 0 && (
        <Box className="alerts-pagination">
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" className="pagination-info">
              Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, pagination.total)} of {pagination.total} alerts
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                onClick={() => handlePageChange(null, pagination.page - 1)}
                disabled={pagination.page <= 1}
                size="small"
              >
                Previous
              </Button>
              
              <Pagination
                count={Math.ceil(pagination.total / pagination.size)}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
              
              <Button
                variant="outlined"
                onClick={() => handlePageChange(null, pagination.page + 1)}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.size)}
                size="small"
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {filteredAlerts.length === 0 && !loading && (
        <Box className="no-alerts">
          <SecurityIcon className="no-alerts-icon" />
          <Typography variant="h6">No alerts found</Typography>
          <Typography variant="body2">
            {searchTerm || agentFilter !== 'all' || levelFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'No security alerts are currently available'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Alerts;
